import { useRef } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FloatingActionButton } from "@/DesignSystem/Molecules/FloatingActionButton";
import { useAIScribeStartConsultation } from "../Hooks/useAIScribeStartConsultation";

interface AIScribeLaunchButtonProps {
    encounterId: string;
    patientContactId: string;
    patientContactFacetId: string;
    className?: string;
    type?: "consultation" | "marketing"
    onAIScribeSubscriptionInactive?: () => void;
    onAIScribeConsultationCreated?: (
        consultationId: string,
        url: string,
        bottomPos: number,
        rightPos: number,
        encounterId: string,
        patientContactId: string,
        patientContactFacetId: string
    ) => void;
    onAIScribeMarketingPopupOpen?: (
        bottomPos: number,
        rightPos: number,
        encounterId: string,
        patientContactId: string,
        patientContactFacetId: string
    ) => void;
}

function AIScribeLaunchButton(props: Readonly<AIScribeLaunchButtonProps>): JSX.Element {
    const { getText } = useTranslation();
    const buttonRef = useRef(null);

    const { startConsultation, isLoading } = useAIScribeStartConsultation({
        encounterId: props.encounterId,
        onAIScribeSubscriptionInactive: props.onAIScribeSubscriptionInactive,
        onAIScribeConsultationCreated: (consultationId, url, encounterId) => {
            const position = getPopupPosition();
            props.onAIScribeConsultationCreated?.(
                consultationId,
                url,
                position.bottom,
                position.right,
                encounterId,
                props.patientContactId,
                props.patientContactFacetId
            );
        }
    });

    function getPopupPosition(): { bottom: number, right: number } {
        if (!buttonRef.current)
            return { bottom: 0, right: 0 };

        const popupRect = buttonRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const bottom = windowHeight - popupRect.bottom;
        const right = windowWidth - popupRect.right;

        return { bottom, right };
    }

    function openMarketingPopup(): void {
        const position = getPopupPosition();
        props.onAIScribeMarketingPopupOpen?.(
            position.bottom,
            position.right,
            props.encounterId,
            props.patientContactId,
            props.patientContactFacetId
        );
    }

    function handleOnClick(event: React.MouseEvent): void {
        event.stopPropagation();

        if (props.type === "consultation")
            startConsultation();
        else if (props.type === "marketing")
            openMarketingPopup();
    }

    return (
        <div ref={buttonRef} className={props.className}>
            <FloatingActionButton
                id="ai-scribe-launch-btn"
                dataCy="ai-scribe-launch-btn"
                size="md"
                onClick={handleOnClick}
                disabled={isLoading}
                label={getText("AIScribe_AIAssistant")}
            />
        </div>
    );
}

export { AIScribeLaunchButton };
